import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const policies = [
  { name: "Lowering Housing Costs", path: "/policies/housing" },
  { name: "Lowering Rental Costs", path: "/policies/rental" },
  { name: "Clean Energy & Jobs", path: "/policies/clean-energy" },
  { name: "Traffic & Non-Violent (TNV)", path: "/policies/tnv" },
  { name: "Ending Food Deserts and Food Swamps", path: "/policies/food-deserts" },
  { name: "No NV Taxpayer Funded AI Data Centers", path: "/policies/no-ai-subsidies" },
  { name: "Protect Jobs From AI Automation", path: "/policies/automation" },
  { name: "eNVy Healthcare", path: "/policies/healthcare" },
  { name: "Pretrial Justice Reform", path: "/policies/bail-reform" },
  { name: "Post-Justice Reform", path: "/policies/post-justice" },
  { name: "NDA Accountability", path: "/policies/nda-reform" },
  { name: "Protect Public Schools", path: "/policies/education" },
  { name: "Federal Funding & Infrastructure", path: "/policies/federal-funding" },
  { name: "Pro-Union", path: "/policies/pro-union" },
  { name: "Pro-Worker, NOT Anti-Business", path: "/policies/pro-worker" },
  { name: "NV Health Insurance Reform", path: "/policies/health-insurance" },
  { name: "Ending Unfair Arbitration", path: "/policies/arbitration" },
  { name: "More Coming Soon", path: "/policies/more-coming-soon" },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Events", path: "/events" },
    { name: "News", path: "/news" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#0B1A3B]/95 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/10"
          : "bg-[#0B1A3B]/80 backdrop-blur-xl border-b border-white/5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="text-xl md:text-2xl font-black tracking-tight text-white">
              COOPER
            </span>
            <span className="text-xs md:text-sm font-bold text-[#D4AF37] tracking-widest">
              FOR NEVADA
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                  location.pathname === link.path
                    ? "text-[#D4AF37]"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {link.name}
              </Link>
            ))}

            {/* Policies Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="policies-dropdown"
                className="flex items-center gap-1 px-4 py-2 text-sm font-bold uppercase tracking-wider text-white/60 hover:text-white transition-colors"
              >
                Solutions <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#0F2251] border border-white/10 shadow-xl shadow-black/20">
                {policies.map((policy) => (
                  <DropdownMenuItem key={policy.path} asChild>
                    <Link
                      to={policy.path}
                      data-testid={`policy-link-${policy.path.split('/').pop()}`}
                      className="cursor-pointer text-white/70 hover:text-[#D4AF37] hover:bg-white/5 focus:bg-white/5 focus:text-[#D4AF37]"
                    >
                      {policy.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* I Need You */}
            <Link
              to="/spread-the-word"
              data-testid="nav-i-need-you"
              className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                location.pathname === "/spread-the-word"
                  ? "text-[#D4AF37]"
                  : "text-[#D4AF37] hover:text-[#F5D76E]"
              }`}
            >
              I Need You
            </Link>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/volunteer"
              data-testid="nav-volunteer-btn"
              className="px-6 py-2.5 text-sm font-bold uppercase tracking-widest border border-white/20 text-white hover:bg-white/10 hover:border-white/40 transition-all"
            >
              Volunteer
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact-btn"
              className="px-6 py-2.5 text-sm font-bold uppercase tracking-widest bg-[#D4AF37] hover:bg-[#B5952F] text-black transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4AF37]/20"
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2 text-white/70"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-[#0F2251] border-t border-white/10" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-sm font-bold uppercase tracking-wider ${
                    location.pathname === link.path
                      ? "text-[#D4AF37] bg-white/5"
                      : "text-white/60"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Policies */}
              <div className="px-4 py-2">
                <p className="text-xs font-bold uppercase tracking-widest text-white/30 mb-2">
                  Solutions
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {policies.map((policy) => (
                    <Link
                      key={policy.path}
                      to={policy.path}
                      className="py-2 text-sm text-white/50 hover:text-[#D4AF37]"
                    >
                      {policy.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* I Need You */}
              <Link
                to="/spread-the-word"
                className={`px-4 py-3 text-sm font-bold uppercase tracking-wider ${
                  location.pathname === "/spread-the-word"
                    ? "text-[#D4AF37] bg-white/5"
                    : "text-[#D4AF37]"
                }`}
              >
                I Need You
              </Link>

              <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-white/10">
                <Link
                  to="/volunteer"
                  className="px-4 py-3 text-sm font-bold uppercase tracking-widest text-center border border-white/20 text-white"
                >
                  Volunteer
                </Link>
                <Link
                  to="/contact"
                  className="px-4 py-3 text-sm font-bold uppercase tracking-widest text-center bg-[#D4AF37] text-black"
                >
                  Contact
                </Link>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
